/* ------------------------------------------------------------------
   CropPreview – video preview (crop, trim, filters, text overlays)
   • React-easy-crop when cropping
   • Loops only the trimmed segment
   • Live CSS filter chain
   • Draggable, contentEditable text overlays
   • ✅ 2025-07 mobile update:
        – Preview frame is always at most 450 px
          and now takes up 80 % of the viewport width
          (auto-updates on rotate / resize).
------------------------------------------------------------------- */
import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { toCdn } from './constants';
import useIsMobile from '../../../../../hooks/useIsMobile';

const MAX_FRAME = 450;   // desktop hard ceiling

/* helper – responsive square side length (80 % of vw, never >450) */
const calcFrameSize = () =>
  Math.min(
    typeof window === 'undefined' ? MAX_FRAME : Math.round(window.innerWidth * 0.8),
    MAX_FRAME,
  );

/* ------------------------------------------------------------------ */
const CropPreview = forwardRef(function CropPreview(
  {
    src,
    /* crop */
    editing = false,
    crop,
    zoom,
    aspect,
    appliedCrop,
    /* trim */
    dims = { w: 0, h: 0 },
    trimStart = 0,
    trimEnd   = null,
    /* filters */
    filterCss = '',
    /* text overlay */
    textItems            = [],
    setTextItems         = () => {},
    selectedTextId       = null,
    setSelectedTextId    = () => {},
    /* callbacks */
    onCropChange,
    onZoomChange,
    onCropComplete,
  },
  ref,
) {
  /* ------------------------------------------------------------------
     0. Responsive preview frame
  ------------------------------------------------------------------ */
  const isMobile = useIsMobile();
  const [frameSize, setFrameSize] = useState(calcFrameSize());

  /* update on orientation / resize – only matters on phones */
  useEffect(() => {
    if (!isMobile) return;
    const handler = () => setFrameSize(calcFrameSize());
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, [isMobile]);

  const EDIT_FRAME = { width: frameSize, height: frameSize, margin: '0 auto' };

  /* ------------------------------------------------------------------
     1. Loop only trimmed segment when not cropping
  ------------------------------------------------------------------ */
  useEffect(() => {
    if (!ref?.current || editing) return;
    const vid = ref.current;

    const seekToStart = () => { vid.currentTime = trimStart; };
    const loopHandler = () => {
      if (trimEnd !== null && vid.currentTime >= trimEnd) vid.currentTime = trimStart;
    };

    vid.addEventListener('loadedmetadata', seekToStart);
    vid.addEventListener('timeupdate',     loopHandler);
    if (vid.readyState >= 1) seekToStart();

    return () => {
      vid.removeEventListener('loadedmetadata', seekToStart);
      vid.removeEventListener('timeupdate',     loopHandler);
    };
  }, [ref, editing, trimStart, trimEnd]);

  /* ------------------------------------------------------------------
     2. Early exit if no source
  ------------------------------------------------------------------ */
  if (!src) return null;

  /* ------------------------------------------------------------------
     3. Helper – render one text overlay div
  ------------------------------------------------------------------ */
  const renderText = (item, interactive = true) => (
    <div
      key={item.id}
      contentEditable={interactive}
      suppressContentEditableWarning
      onBlur={e => {
        const html = e.target.innerHTML;
        setTextItems(prev => prev.map(t => (t.id === item.id ? { ...t, html } : t)));
      }}
      onMouseDown={interactive ? e => handleDragStart(e, item) : null}
      style={{
        position: 'absolute',
        top: item.y,
        left: item.x,
        transform: 'translate(-50%,-50%)',
        fontSize: item.fontSize,
        fontFamily: item.fontFamily,
        color: item.color,
        backgroundColor: item.bgColor,
        padding: '6px 10px',
        borderRadius: 6,
        whiteSpace: 'pre-wrap',
        textAlign: 'center',
        cursor: interactive ? 'move' : 'default',
        outline: selectedTextId === item.id && interactive ? '1px dashed #09f' : 'none',
        userSelect: 'text',
        filter: filterCss,
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      dangerouslySetInnerHTML={{ __html: item.html }}
    />
  );

  /* ------------------------------------------------------------------
     4. Drag handlers for text boxes
  ------------------------------------------------------------------ */
  const handleDragStart = (e, item) => {
    setSelectedTextId(item.id);
    const startX = e.clientX, startY = e.clientY;
    const initX  = item.x,    initY  = item.y;

    const move = ev => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setTextItems(prev => prev.map(t =>
        t.id === item.id ? { ...t, x: initX + dx, y: initY + dy } : t
      ));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup',   up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup',   up);
  };

  /* ------------------------------------------------------------------
     5. CROP MODE (react-easy-crop)
  ------------------------------------------------------------------ */
  if (editing) {
    return (
      <div className="position-relative overflow-hidden" style={EDIT_FRAME}>
        <Cropper
          mediaType="video"
          video={toCdn(src)}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          objectFit="contain"
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropComplete}
          style={{ width:'100%', height:'100%' }}
          videoProps={{
            muted: true, autoPlay: true, loop: true, playsInline: true, controls: false,
            style: { filter: filterCss },
          }}
        />
        {textItems.map(item => renderText(item,false))}
      </div>
    );
  }

  /* ------------------------------------------------------------------
     6. Helper – build cropped frame + transform
  ------------------------------------------------------------------ */
  const buildCroppedFrame = () => {
    const MAX = frameSize;
    const cropAspect = appliedCrop.width / appliedCrop.height;

    const frame = cropAspect >= 1
      ? { width: MAX, height: MAX / cropAspect }
      : { width: MAX * cropAspect, height: MAX };

    const scale   = frame.width / appliedCrop.width;
    const offsetX = (frame.width  - appliedCrop.width  * scale) / 2;
    const offsetY = (frame.height - appliedCrop.height * scale) / 2;

    const style = {
      transform: `translate(${
        -appliedCrop.x * scale + offsetX
      }px, ${-appliedCrop.y * scale + offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width : dims.w,
      height: dims.h,
      filter: filterCss,
    };

    return { frame, style };
  };

  /* ------------------------------------------------------------------
     7. CROPPED PREVIEW
  ------------------------------------------------------------------ */
  if (appliedCrop && dims.w > 0 && dims.h > 0) {
    const { frame, style } = buildCroppedFrame();
    return (
      <div className="overflow-hidden position-relative d-flex" style={{ ...frame, margin:'0 auto' }}>
        <video ref={ref} src={toCdn(src)} muted autoPlay playsInline style={style}/>
        {textItems.map(item => renderText(item))}
      </div>
    );
  }

  /* ------------------------------------------------------------------
     8. DEFAULT (no crop yet)
  ------------------------------------------------------------------ */
  return (
    <div className="overflow-hidden position-relative" style={EDIT_FRAME}>
      <video
        ref={ref}
        src={toCdn(src)}
        muted
        autoPlay
        playsInline
        style={{ width:'100%', height:'100%', objectFit:'contain', filter:filterCss }}
      />
      {textItems.map(item => renderText(item))}
    </div>
  );
});

/* ------------------------------------------------------------------
   propTypes
------------------------------------------------------------------- */
CropPreview.propTypes = {
  src              : PropTypes.string,
  editing          : PropTypes.bool,
  crop             : PropTypes.object,
  zoom             : PropTypes.number,
  aspect           : PropTypes.number,
  appliedCrop      : PropTypes.object,
  dims             : PropTypes.object,
  trimStart        : PropTypes.number,
  trimEnd          : PropTypes.number,
  filterCss        : PropTypes.string,
  textItems        : PropTypes.array,
  setTextItems     : PropTypes.func,
  selectedTextId   : PropTypes.string,
  setSelectedTextId: PropTypes.func,
  onCropChange     : PropTypes.func,
  onZoomChange     : PropTypes.func,
  onCropComplete   : PropTypes.func,
};

export default CropPreview;
